// @flow
import { List, Map as ImmutableMap } from 'immutable';

import { mkInstitutionLogin, mkInstitutionLoginAggregator, mkMfaChallenge, mkMfaChallengeItem, mkMfaAnswerChoice }
  from 'data/institutionLogins/types';
import { isAcme } from 'isAcme';

import type { Credential, InstitutionLogin } from './types';

export const transformResponseToInstitutionLogins = (response: any): Array<InstitutionLogin> => (
  response.data.resources.map((data) => (
    mkInstitutionLogin({
      id: data.id,
      name: data.name,
      isDeleted: data.isDeleted,

      institutionId: data.institutionId,
      brandingId: data.brandingId,
      cpInstitutionId: data.cpInstitutionId,

      // TODO: this belongs on QCS, shouldn't be hardcoded here!
      ewcLive: !isAcme && data.brandingId && data.brandingId === '3000',

      aggregators: !data.aggregators ? null :
        List(data.aggregators.map((aggregator) => (mkInstitutionLoginAggregator({
          channel: aggregator.cpChannel || 'FDS_DEFAULT',

          aggStatus: aggregator.aggStatus,
          aggStatusCode: aggregator.aggStatusCode,
          aggStatusDetail: aggregator.aggStatusDetail,

          lastStatusUpdatedAt: aggregator.lastStatusUpdatedAt,

          lastRefreshAttemptedAt: aggregator.lastRefreshAttemptedAt,
          lastRefreshSuccessfulAt: aggregator.lastRefreshSuccessfulAt,
        })))),
    })
  ))
);

export const transformInstitutionLoginToRequestData = (institutionLogin: InstitutionLogin) => ({
  id: institutionLogin.id ? institutionLogin.id : null,
  cpSetupMode: 'SKIP_ACCOUNT_DISCOVERY',

  name: institutionLogin.name ? institutionLogin.name : null,
  aggregators: !institutionLogin.aggregators || institutionLogin.aggregators.size === 0 ? null :
    [{
      cpChannel: institutionLogin.aggregators.get(0).channel,
    }],

  // todo: should we support clearing of brandingId and/or institutionId - (deactivation)
  // todo: what about setting of brandingId and/or institutionId - (activation)
  // todo: what about changing of brandingId and/or institutionId - (i.e. deactivation - reactivation)
});

export const transformRefreshingLoginsToRequestData = (
  logins: Array<InstitutionLogin>,
  credentialsBlobsForRefreshingLogins: ImmutableMap<string, string>,
  credentialsForRefreshingLogins: ImmutableMap<string, List<Credential>>,
) => {
  const loginRefreshCredentials = [];
  if (credentialsBlobsForRefreshingLogins.size > 0) {
    credentialsBlobsForRefreshingLogins.forEach((credentialsBlob, institutionLoginId) => {
      if (logins.find((login) => login.id === institutionLoginId)) {
        loginRefreshCredentials.push({
          institutionLoginId,
          credentialsBlob,
        });
      }
    });
  }
  if (credentialsForRefreshingLogins.size > 0) {
    credentialsForRefreshingLogins.forEach((credentials, institutionLoginId) => {
      if (logins.find((login) => login.id === institutionLoginId)) {
        const tCredentials: Array<*> = credentials.toArray().map((credential) =>
          ({ name: credential.key, key: credential.key, value: credential.value }));
        loginRefreshCredentials.push({
          institutionLoginId,
          credentials: tCredentials,
        });
      }
    });
  }
  return {
    // doIgnoreThrottle: true,
    loginRefreshCredentials: loginRefreshCredentials.length > 0 ? loginRefreshCredentials : null,
  };
};

export const transformMfaResponseToMfaChallenge = (institutionLoginId: string, mfaResponse: any, submitUrl: string) => (
  mkMfaChallenge({
    institutionLoginId,
    challenges: List(mfaResponse.map((challenge) => {
      if (challenge.type === 'IMAGE') {
        return mkMfaChallengeItem({
          key: challenge.key,
          sequence: challenge.sequence,
          question: challenge.displayText,
          base64Image: challenge.image,
          answerChoices: !challenge.answerChoices ? null :
            List(challenge.answerChoices.map((answerChoice) => (
              mkMfaAnswerChoice({
                displayText: answerChoice.displayText,
              })
            ))),
        });
      }
      return mkMfaChallengeItem({
        key: challenge.key,
        sequence: challenge.sequence,
        question: challenge.question,
        answerChoices: !challenge.answerChoices ? null :
          List(challenge.answerChoices.map((answerChoice) => (
            mkMfaAnswerChoice({
              displayText: answerChoice.displayText,
            })
          ))),
      });
    })),
    submitUrl,
  })
);

